import {
    ChangeDetectionStrategy,
    Component,
    ContentChild,
    EventEmitter,
    Input,
    Output,
    Self,
    TemplateRef,
    OnInit,
    ChangeDetectorRef,
} from '@angular/core';
import { NgControl, FormControl } from '@angular/forms';
import {
    catchError,
    combineLatestWith,
    debounceTime,
    delay,
    map,
    switchMap,
    tap,
} from 'rxjs/operators';
import { UntilDestroy } from '@ngneat/until-destroy';
import { BehaviorSubject, combineLatest, EMPTY, merge, Observable, of, shareReplay } from 'rxjs';
import { EntData, EntList, SearchSettings } from '@bazis/shared/models/srv.types';
import { BazisEntityService } from '@bazis/shared/services/entity.service';
import { MapLayers, MapSettings } from '@bazis/map/models/map.models';
import { TemplateObservable } from '@bazis/shared/classes/template-observable';
import { EntityFormControl } from '@bazis/form/models/form.types';
import { BazisFormService } from '@bazis/form/services/form.service';
import { ALL_LIST_LIMIT, BazisConfigurationService } from '@bazis/configuration.service';
import { BazisControlValueAccessor } from '@bazis/form/components/control-value-accessor.component';

export type MapSelectMode = 'map' | 'list' | 'both';

@UntilDestroy()
@Component({
    selector: 'bazis-select-on-map',
    templateUrl: './select-on-map.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BazisSelectOnMapComponent extends BazisControlValueAccessor implements OnInit {
    @Input() list$: Observable<EntData[]>;

    // дефолтный режим компонента
    @Input() mode: MapSelectMode = 'map';

    @Input() limit: number = ALL_LIST_LIMIT;

    // можем ли менять режим
    @Input() canToggleMode: boolean = true;

    // можем ли искать или показываем список целиком
    @Input() canSearch: boolean = true;

    // происходит ли выбор объекта по клику на листе
    @Input() canSelectFromList: boolean = true;

    // настройки карты
    @Input() mapSettings: TemplateObservable<Partial<MapSettings>> = null;

    // слои
    @Input() mapLayers: TemplateObservable<MapLayers> = null;

    // id слоя, объекты которого выбираются
    @Input() searchLayerId: string = null;

    // настройки, если формируем список внутри компонента
    @Input() searchSettings: SearchSettings = null;

    // настройки, если формируем список внутри компонента
    @Input() processListObject: Function = (obj: EntData) => obj.$snapshot;

    // настройки, если формируем список внутри компонента
    @Input() filterList: Function = (obj: EntData) => obj.$snapshot.point;

    // Кнопка добавления объекта
    @Input() createButton: TemplateRef<any>;

    @Output() searchChanged: EventEmitter<string> = new EventEmitter();

    @Output() listUpdated: EventEmitter<EntData[]> = new EventEmitter();

    @ContentChild(TemplateRef) itemTpl: TemplateRef<any>;

    search = new FormControl('');

    isLoading = false;

    selectedId$ = new BehaviorSubject(null);

    selectOnMap: TemplateObservable<{ layerId: string; objectId: string; openPopup?: boolean }> =
        new TemplateObservable(null);

    searchValue$ = this.search.valueChanges.pipe(
        debounceTime(200),
        tap((value) => {
            if (!this.searchSettings) {
                this.searchChanged.emit(value);
            }
        }),
        shareReplay(),
    );

    mapLayers$;

    updateAfterSelectedId$;

    _inited$ = new BehaviorSubject(true);

    constructor(
        @Self() public ngControl: NgControl,
        protected cdr: ChangeDetectorRef,
        configurationService: BazisConfigurationService,
        private entityService: BazisEntityService,
    ) {
        super(ngControl, cdr, configurationService);
        ngControl.valueAccessor = this;
    }

    ngOnInit() {
        if (this.ngControl.control instanceof EntityFormControl) {
            BazisFormService.getPropertiesFromConfig(this, this.ngControl.control.$config);
        }

        if (this.searchSettings.limit) this.limit = this.searchSettings.limit;

        const componentListObservable$ = merge(this.searchValue$, this._inited$).pipe(
            switchMap(() => this.load()),
            map((result: EntList) => {
                if (!result.list) return [];
                return result.list.filter((object) => this.filterList(object));
            }),
            shareReplay(),
        );
        this.list$ = this.list$ || componentListObservable$;

        this.mapLayers$ = combineLatest([this.list$, this.selectedId$, this.mapLayers.$]).pipe(
            map(([list, selectedId, layers]) => {
                Object.keys(layers).forEach((layerId) => {
                    if (layerId === this.searchLayerId) {
                        layers[layerId].objects = list
                            .map((object) => {
                                const newObject = this.processListObject(object);
                                if (newObject.id === selectedId) {
                                    newObject.icon = {
                                        default: { ...layers[layerId].icon.selected },
                                        selected: { ...layers[layerId].icon.selected },
                                    };
                                }
                                return newObject;
                            })
                            .filter((object) => object.geometry);
                        layers[layerId].updatedTime = new Date().getTime();
                    }
                });
                return layers;
            }),
            shareReplay(),
        );

        this.updateAfterSelectedId$ = this.selectedId$.pipe(
            combineLatestWith(this.mapLayers$),
            delay(1),
            tap(([selectedId, mapLayers]) => {
                if (selectedId && mapLayers) {
                    this.selectOnMap.set({
                        layerId: this.searchLayerId,
                        objectId: selectedId,
                        openPopup: true,
                    });
                }
            }),
        );
    }

    public setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.search.disable();
        } else {
            this.search.enable();
        }
    }

    changeField(value) {
        this.onChange(value);
    }

    public writeValue(value): void {
        this.selectedId$.next(value);
        this.search.setValue('', { emitEvent: false });
    }

    trackById(index, item) {
        return item.id;
    }

    load(): Observable<any> {
        if (!this.searchSettings) return EMPTY;
        this.isLoading = true;
        return this.entityService
            .getEntityList$(this.searchSettings.entityType, {
                suffix: this.searchSettings.suffix || '',
                search: this.search.value || '',
                params: this.searchSettings.params || {},
                fields: this.searchSettings.fields
                    ? {
                          [this.searchSettings.entityType]: this.searchSettings.fields,
                      }
                    : null,
                limit: this.limit,
            })
            .pipe(catchError((e) => of({ list: [] })));
    }

    setMode(mode) {
        this.mode = mode;
        window.dispatchEvent(new Event('resize'));
    }

    selectFromList(entityId: string) {
        this.selectedId$.next(entityId);
        this.changeField(entityId);
    }

    processPopupAction(popupAction) {
        if (!popupAction) return;
        if (popupAction.event && popupAction.event.action === 'select' && popupAction.objectId) {
            this.selectedId$.next(popupAction.objectId);
            this.changeField(popupAction.objectId);
        }
    }
}
